import { useCallback, useMemo, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridReadyEvent,
  GridApi,
  ValueFormatterParams,
  ICellRendererParams,
  IDatasource,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as XLSX from 'xlsx';
import CommonButton from '../../../components/common-components/button/CommonButton';
import styles from './HospitalFiles.module.css';
import { localeText } from '../../../constants/AgGridLocale';
import { Pencil, Trash2 } from 'lucide-react';
import NewRowManuel from './parts/new-data/new-row/manuel/NewRowManuel';
import NewRowQrCode from './parts/new-data/new-row/qr-code/NewRowQrCode';
import NewHospital from './parts/new-data/new-hospital/NewHospital';
import EditRowManually from './parts/edit-data/EditRowManually';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import SwitchHospital from './parts/switch-data/SwitchHospital';
import { IHospitalRecordResult } from '../../../models/hospital/HospitalModel';
import DeleteConfirmationDialog from './parts/delete/DeleteConfirmationDialog';
import NewRowFromKn from './parts/new-data/new-row/from-kn/NewRowFromKn';
import NewTagNumber from './parts/new-data/new-tag/NewTagNumber';
import { RotateCw } from 'lucide-react';

const ActionCellRenderer = (props: ICellRendererParams<IHospitalRecordResult>) => {
  const { popupStore, hospitalFilesStore } = useStore();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      hospitalFilesStore.setSelectedRecord(props.data);
      popupStore.setIsEditManuelRowOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data?.id) {
      hospitalFilesStore.setSelectedRecordToDelete(props.data.id);
      popupStore.setIsDeleteConfirmationOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button onClick={handleDelete} className={styles.deleteButton} title="Sil">
        <Trash2 size={20} />
      </button>
    </div>
  );
};

const HospitalFiles = () => {
  const [gridApi, setGridApi] = useState<GridApi<IHospitalRecordResult> | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const { popupStore, hospitalFilesStore } = useStore();

  const colorItems = useMemo<{ key: string; label: string; dot: string; value: number }[]>(() => {
    const map = new Map<string, number>();
    (hospitalFilesStore.tagColors || []).forEach(c => {
      if (c?.color) map.set(c.color.toLowerCase(), c.sequence ?? 0);
    });
    return [
      { key: 'red', label: 'kırmızı', dot: '#ef4444' },
      { key: 'yellow', label: 'sarı', dot: '#eab308' },
      { key: 'green', label: 'yeşil', dot: '#22c55e' },
    ].map(x => ({ ...x, value: map.get(x.key) ?? 0 }));
  }, [hospitalFilesStore.tagColors]);

  useEffect(() => {
    if (!hospitalFilesStore.selectedHospital) {
      popupStore.setIsSwitchHospitalOpen(true);
    }
    // start SignalR live updates when page is open
    hospitalFilesStore.startLiveTagColors();
    return () => {
      hospitalFilesStore.stopLiveTagColors();
    };
  }, []);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    hospitalFilesStore.getTagColors();
  }, [hospitalFilesStore.selectedTag?.id]);
  const dataSource = useMemo((): IDatasource => {
    if (!hospitalFilesStore.selectedHospital) {
      return {
        getRows: async () => {
          return;
        },
      };
    }
    return {
      getRows: async params => {
        try {
          const result = await hospitalFilesStore.listHospitalRecord({
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
          });
          if (result) {
            params.successCallback(result.rows, result.lastRow);
          } else {
            params.failCallback();
          }
        } catch (error) {
          console.error('Data loading error:', error);
          params.failCallback();
        }
      },
    };
  }, [hospitalFilesStore, hospitalFilesStore.selectedHospital]);
  const onGridReady = useCallback(
    (params: GridReadyEvent<IHospitalRecordResult>) => {
      setGridApi(params.api);
      hospitalFilesStore.setGridApi(params.api);
    },
    [hospitalFilesStore]
  );

  const exportToExcel = useCallback(async () => {
    if (!gridApi) return;

    gridApi.showLoadingOverlay();

    try {
      const result = await hospitalFilesStore.listHospitalRecord({
        startRow: 0,
        endRow: 1000000,
        filterModel: gridApi.getFilterModel(),
        sortModel: null,
      });

      if (!result?.rows.length) {
        gridApi.hideOverlay();
        return;
      }

      const exportData = result.rows.map(item => ({
        ID: item.id,
        Hastane: item.hospitalName,
        'Cihaz Tanımı': item.biomedicalDeviceDescription,
        Marka: item.brand,
        Model: item.model,
        'Seri No': item.serialNumber,
        'Künye No': item.identificationNumber,
        Lokasyon: item.location,
        'Etiket No': item.tagNumberWithColor,
        'Eklenme Tarihi': new Date(item.addDate).toLocaleDateString('tr-TR'),
        'Zimmetli Kişi': item.responsiblePerson,
        Notlar: item.note,
        'Ekleyen Kişi': item.createdBy,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Hastane Cihazları');
      const hospitalName = hospitalFilesStore.selectedHospital?.hospitalName || 'Hastane';
      const sanitizedHospitalName = hospitalName
        .replace(/[ıİ]/g, 'I')
        .replace(/[ğĞ]/g, 'G')
        .replace(/[üÜ]/g, 'U')
        .replace(/[şŞ]/g, 'S')
        .replace(/[öÖ]/g, 'O')
        .replace(/[çÇ]/g, 'C')
        .replace(/[^a-zA-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = sanitizedHospitalName
        ? `${sanitizedHospitalName}_Hastane_Cihazlari_${dateStr}.xlsx`
        : `Hastane_Cihazlari_${dateStr}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      gridApi.hideOverlay();
    }
  }, [gridApi, hospitalFilesStore]);

  const columnDefs = useMemo<ColDef<IHospitalRecordResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 120,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: ActionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 100,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'tagNumberWithColor',
        headerName: 'Etiket No',
        minWidth: 130,
        filter: false,
        sortable: false,
      },
      {
        field: 'biomedicalDeviceDescription',
        headerName: 'Cihaz Tanımı',
        minWidth: 200,
        flex: 1.5,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'brand',
        headerName: 'Marka',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'model',
        headerName: 'Model',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'serialNumber',
        headerName: 'Seri No',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'identificationNumber',
        headerName: 'Künye No',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'location',
        headerName: 'Lokasyon',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'addDate',
        headerName: 'Eklenme Tarihi',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString('tr-TR');
        },
      },
      {
        field: 'note',
        headerName: 'Notlar',
        minWidth: 200,
        flex: 2,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'hospitalName',
        headerName: 'Hastane',
        minWidth: 200,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'responsiblePerson',
        headerName: 'Zimmetli Kişi',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'createdBy',
        headerName: 'Ekleyen Kişi',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    }),
    []
  );
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;
  const gridRowHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const gridHeaderHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const pageSize = isMobile ? 10 : 20;

  const handleReadFromQr = () => {
    popupStore.setIsNewQrCodeOpen(true);
  };

  const handleNewManuelRow = () => {
    popupStore.setIsNewManuelRowOpen(true);
  };

  const handleSwitchHospital = () => {
    popupStore.setIsSwitchHospitalOpen(true);
  };
  const handleNewKnRow = () => {
    popupStore.setIsNewKnRowOpen(true);
  };

  const handleRefresh = () => {
    hospitalFilesStore.refreshGrid();
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.mainTitle}>Hastane Dosyaları</h2>
          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Seçilen Hastane:</span>
              <span className={styles.infoValue}>
                {hospitalFilesStore.selectedHospital?.hospitalName}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Seçilen Etiket No:</span>
              <span className={styles.infoValue}>{hospitalFilesStore.selectedTag?.prefix}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Güncel Etiket No Değerleri:</span>
              <div className={styles.tagColorList}>
                {colorItems.map(item => (
                  <span key={item.key} className={styles.tagColorItem}>
                    <span className={styles.colorDot} style={{ backgroundColor: item.dot }} />
                    <span className={styles.tagColorLabel}>{item.label}:</span>
                    <span className={styles.tagColorValue}>{item.value}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <CommonButton type="primary" onClick={handleSwitchHospital}>
              Hastane Değiştir
            </CommonButton>
            <CommonButton type="primary" onClick={exportToExcel}>
              Excel'e Aktar
            </CommonButton>
            <CommonButton type="primary" onClick={handleNewManuelRow}>
              Yeni Veri Girişi
            </CommonButton>
            <CommonButton type="primary" onClick={handleNewKnRow}>
              Künye Numarası İle Veri Girişi
            </CommonButton>
            <CommonButton type="tertiary" onClick={handleReadFromQr}>
              QR'dan Oku
            </CommonButton>
            <CommonButton
              type="secondary"
              onClick={handleRefresh}
              leftIcon={<RotateCw size={18} />}
            >
              Yenile
            </CommonButton>
          </div>
        </div>

        <div className={styles.gridContainer}>
          <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              rowModelType={'infinite'}
              cacheBlockSize={20}
              maxBlocksInCache={1}
              datasource={dataSource}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={pageSize}
              rowSelection="multiple"
              rowHeight={gridRowHeight}
              headerHeight={gridHeaderHeight}
              animateRows={true}
              suppressColumnVirtualisation={true}
              suppressRowVirtualisation={true}
              localeText={localeText}
              getRowStyle={params => {
                const color = params.data?.color;
                if (color === 'red') {
                  return { backgroundColor: '#ffebee' };
                } else if (color === 'yellow') {
                  return { backgroundColor: '#fff8e1' };
                } else if (color === 'green') {
                  return { backgroundColor: '#e8f5e8' };
                }
                return undefined;
              }}
            />
          </div>
        </div>
      </div>
      {popupStore.isNewManuelRowOpen && <NewRowManuel />}
      {popupStore.isNewQrCodeRowOpen && <NewRowQrCode />}
      {popupStore.isEditManuelRowOpen && <EditRowManually />}
      {popupStore.isSwitchHospitalOpen && <SwitchHospital />}
      {popupStore.isNewKnRowOpen && <NewRowFromKn />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteConfirmationOpen}
        onClose={() => popupStore.setIsDeleteConfirmationOpen(false)}
        onConfirm={async () => {
          if (hospitalFilesStore.selectedRecordToDelete) {
            await hospitalFilesStore.deleteRecord(hospitalFilesStore.selectedRecordToDelete);
            hospitalFilesStore.setSelectedRecordToDelete(null);
          }
          popupStore.setIsDeleteConfirmationOpen(false);
        }}
        title="Kayıt Silme Onayı"
      />
    </>
  );
};

export default observer(HospitalFiles);
